import {
  LayoutGrid,
  Inbox,
  ClipboardList,
  CircleCheckBig,
  Sparkles,
  Telescope,
  Shield,
  Sliders,
  Settings,
  Search,
  UserCircle,
  LogOut,
  LoaderCircle,
  CreditCard,
  Plug,
  ChevronUp,
  LifeBuoy,
  Radar,
  BrainCircuit,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "../ui/sidebar";
import type { RouteName } from "../../lib/router";
import { ThemeToggle } from "./ThemeToggle";
import { OrgSwitcher } from "./OrgSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { BrandLockup } from "../brand/BrandLockup";
import { BrandMark } from "../brand/BrandMark";
import { getStoredUser } from "../../lib/authStorage";
import type { OrgRoleView } from "../../lib/roleViews";
import { SetupGuide } from "../onboarding/SetupGuide";
import {
  SupportMenuItem,
  supportConfigurationFromEnvironment,
} from "./SupportMenuItem";

type NavItem = {
  label: string;
  path: string;
  icon: typeof Inbox;
  matches: RouteName[];
  allowedRoleViews?: OrgRoleView[];
  exactPath?: string;
};

const navGroups: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "Monitor",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: LayoutGrid, matches: ["dashboard"] },
      { label: "Obligations", path: "/obligations", icon: Radar, matches: ["obligations"] },
      { label: "Rescue Room", path: "/rescues", icon: LifeBuoy, matches: ["rescues"] },
    ],
  },
  {
    label: "Act",
    items: [
      { label: "Inbox", path: "/inbox", icon: Inbox, matches: ["inbox", "thread", "customer"] },
      { label: "Review queue", path: "/review", icon: ClipboardList, matches: ["review", "entity"] },
      { label: "Approved work", path: "/lens/execution", icon: CircleCheckBig, matches: ["lens"], exactPath: "/lens/execution" },
      { label: "Leads", path: "/leads", icon: Sparkles, matches: ["leads"] },
      { label: "Lenses", path: "/lens/support", icon: Telescope, matches: ["lens"] },
    ],
  },
  {
    label: "Manage",
    items: [
      { label: "Integrations", path: "/settings/integrations", icon: Plug, matches: ["integrations"], allowedRoleViews: ["manager", "executive"] },
      { label: "Triage rules", path: "/triage-rules", icon: Sliders, matches: ["triageRules"], allowedRoleViews: ["manager", "executive"] },
      { label: "SLA rules", path: "/sla-rules", icon: Sliders, matches: ["slaRules"], allowedRoleViews: ["manager", "executive"] },
      { label: "AI Quality", path: "/ai-evaluation", icon: BrainCircuit, matches: ["aiEvaluation"] },
      { label: "Security", path: "/security", icon: Shield, matches: ["security"] },
      { label: "Settings", path: "/settings", icon: Settings, matches: ["settings"] },
    ],
  },
];

type AppSidebarProps = {
  routeName: RouteName;
  currentPath: string;
  roleView: OrgRoleView;
  navigate: (route: string) => void;
  onOpenSearch?: () => void;
  onSignOut: () => void;
  isSigningOut?: boolean;
  orgId?: string;
};

export function AppSidebar({
  routeName,
  currentPath,
  roleView,
  navigate,
  onOpenSearch,
  onSignOut,
  isSigningOut = false,
  orgId,
}: AppSidebarProps) {
  const user = getStoredUser();
  const accountLabel = user?.name || user?.email || "Account";
  const supportConfiguration = supportConfigurationFromEnvironment(import.meta.env);
  const visibleNavGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => !item.allowedRoleViews || item.allowedRoleViews.includes(roleView)
      ),
    }))
    .filter((group) => group.items.length > 0);
  return (
    <>
      <Sidebar collapsible="icon" variant="sidebar" className="border-r border-sidebar-border">
        <SidebarHeader className="border-b border-sidebar-border p-2 group-data-[collapsible=icon]:px-0">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                tooltip="IO Spoke"
                aria-label="IO Spoke home"
                className="hover:bg-transparent active:bg-transparent data-[state=open]:hover:bg-transparent"
                onClick={() => navigate("/dashboard")}
              >
                <BrandMark className="hidden size-8 group-data-[collapsible=icon]:block dark:[filter:drop-shadow(1px_0_0_white)_drop-shadow(-1px_0_0_white)_drop-shadow(0_1px_0_white)_drop-shadow(0_-1px_0_white)]" />
                <span className="inline-flex px-1 py-0.5 group-data-[collapsible=icon]:hidden">
                  <BrandLockup className="h-7 w-auto" />
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {onOpenSearch ? (
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Search" aria-label="Search" onClick={onOpenSearch}>
                  <Search />
                  <span className="group-data-[collapsible=icon]:hidden">Search</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ) : null}
          </SidebarMenu>
          <OrgSwitcher />
        </SidebarHeader>
        <SidebarContent className="py-1">
          {visibleNavGroups.map((group) => (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        tooltip={item.label}
                        aria-label={item.label}
                        isActive={
                          item.exactPath
                            ? currentPath === item.exactPath
                            : item.matches.includes(routeName) && currentPath !== "/lens/execution"
                        }
                        onClick={() => navigate(item.path)}
                      >
                        <Icon />
                        <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border group-data-[collapsible=icon]:px-0">
          <SidebarMenu>
            {roleView === "executive" ? <SetupGuide orgId={orgId} navigate={navigate} /> : null}
            <SupportMenuItem configuration={supportConfiguration} />
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton tooltip="Account" aria-label={`Account: ${accountLabel}`}>
                    <UserCircle />
                    <span className="group-data-[collapsible=icon]:hidden">{accountLabel}</span>
                    <ChevronUp aria-hidden="true" className="ml-auto group-data-[collapsible=icon]:hidden" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium">{user?.name ?? "Account"}</span>
                      {user?.email ? (
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      ) : null}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/settings")}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    Account
                  </DropdownMenuItem>
                  {roleView === "executive" ? (
                    <DropdownMenuItem onClick={() => navigate("/billing")}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      Billing
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled={isSigningOut} aria-busy={isSigningOut} onSelect={(event) => { event.preventDefault(); onSignOut(); }}>
                    {isSigningOut ? <LoaderCircle aria-hidden="true" className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" /> : <LogOut className="mr-2 h-4 w-4" />}
                    {isSigningOut ? "Signing out…" : "Sign out"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <ThemeToggle />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarRail />
    </>
  );
}
